import { AlertCircle, ShieldCheck } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface DraftConflictAlertProps {
  onReload: () => void
  onOverwrite: () => void
}

export function DraftConflictAlert({
  onReload,
  onOverwrite,
}: DraftConflictAlertProps) {
  return (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>This draft changed elsewhere</AlertTitle>
      <AlertDescription>
        <div className="flex flex-col gap-2">
          <span>
            It was updated in another tab or window since you started editing
            here. Autosave is paused — keep your changes, or load the latest
            saved version.
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onReload}
            >
              Load latest version
            </Button>
            <Button type="button" size="sm" onClick={onOverwrite}>
              Keep my changes
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  )
}

interface DomainSendDisclosureDialogProps {
  open: boolean
  disabled: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

export function DomainSendDisclosureDialog({
  open,
  disabled,
  onOpenChange,
  onConfirm,
}: DomainSendDisclosureDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            Team visibility
          </DialogTitle>
          <DialogDescription>
            You&apos;re sending from a connected mailbox. This message,
            delivery details, and any replies can appear in Cortado for
            teammates who can view communications.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          Confirm that this business communication is appropriate for your team
          workspace. If your business serves EU, UK, or other privacy-regulated
          contacts, make sure your own customer notices cover team access to
          these messages.
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={disabled}>
            I understand, send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
